/**
 * SwapPage - Dedicated page for token swapping
 *
 * Features:
 * - Full-page swap interface
 * - SOL/USDC swapping via Jupiter Aggregator
 * - Responsive design with mobile/desktop layouts
 */

import React, { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import AppLayout from '../shared/AppLayout';
import { useDesktopView } from '../../hooks/useDesktopView';
import SwapTab from '../Wallet/SwapTab';

// Lazy load desktop component
const DesktopSwapPage = lazy(() => import('./desktop/DesktopSwapPage'));

// Loading fallback for desktop
const DesktopLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Mobile Swap Page Component
const MobileSwapPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  return (
    <AppLayout title="Swap">
      {/* Error/Success Messages */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4"
        >
          <p className="text-red-400 text-sm">{error}</p>
        </motion.div>
      )}
      {success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4"
        >
          <p className="text-green-400 text-sm">{success}</p>
        </motion.div>
      )}

      <SwapTab
        onError={setError}
        onSuccess={setSuccess}
        isDesktop={false}
      />
    </AppLayout>
  );
};

// Main SwapPage component with responsive switching
const SwapPage: React.FC = () => {
  const { isDesktop } = useDesktopView();

  if (isDesktop) {
    return (
      <Suspense fallback={<DesktopLoadingFallback />}>
        <DesktopSwapPage />
      </Suspense>
    );
  }

  return <MobileSwapPage />;
};

export default SwapPage;
